import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { clearSignOutReason } from '@/lib/authExpiry';
import {
  CognitoAuthError, cognitoPool, startSignIn, submitCode, type AuthErrorKind, type CodeChallenge,
} from '@/lib/cognito';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Sign in with an emailed code (login-console-signin; owner 2026-10-19: our own screen).
 * Email, then the code Cognito sends, then core verifies the access token. A send is only
 * ever made by a click: Cognito counts attempts per person, so nothing here retries one.
 * An unknown email behaves exactly like a known one (the pool does not say which).
 *
 * Copy approved by the owner, 2026-10-19 (the same strings as the app's auth.signIn.code).
 */
const ERRORS: Record<AuthErrorKind, string> = {
  limit: 'Too many attempts. Try again shortly.',
  code: 'That code is not right. Check it and try again.',
  expired: 'That code has expired. Send a new one.',
  'signed-out': 'Your session ended. Sign in again.',
  unavailable: 'Could not reach the sign-in service. Try again shortly.',
  other: 'Sign-in did not complete. Try again.',
};

export function CodeSignIn() {
  const navigate = useNavigate();
  const { signInWithCognito } = useAuth();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<CodeChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = (e: unknown) => setError(ERRORS[e instanceof CognitoAuthError ? e.kind : 'other']);

  const send = async () => {
    const pool = cognitoPool();
    if (!pool || !email.trim()) return;
    setBusy(true); setError(null);
    try {
      setChallenge(await startSignIn(pool, email));
      setCode('');
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const verify = async () => {
    const pool = cognitoPool();
    if (!pool || !challenge || !code.trim()) return;
    setBusy(true); setError(null);
    try {
      const tokens = await submitCode(pool, challenge, code);
      const outcome = await signInWithCognito(tokens);
      if (outcome === 'unlinked') { setChallenge(null); return; }
      clearSignOutReason();
      // RequireAdvisor sends a role below advisor on to /not-authorised.
      navigate('/', { replace: true });
    } catch (e) {
      fail(e);
      // An expired code or ended session needs a new code: back to sending one.
      if (e instanceof CognitoAuthError && e.kind === 'expired') setChallenge(null);
    } finally { setBusy(false); }
  };

  if (!challenge) {
    return (
      <div className="flex flex-col gap-4" data-testid="code-signin-email-step">
        <Input
          data-testid="signin-email"
          type="email" autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void send(); }}
          placeholder="Your email address"
        />
        {error ? <p data-testid="signin-error" className="text-sm text-flagged">{error}</p> : null}
        <Button data-testid="send-code-btn" disabled={busy || !email.trim()} onClick={() => void send()}>
          {busy ? 'Sending…' : 'Email me a code'}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4" data-testid="code-signin-code-step">
      <p className="text-sm text-text-secondary">We sent a code to {challenge.username}.</p>
      <Input
        data-testid="signin-code"
        inputMode="numeric" autoComplete="one-time-code" value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void verify(); }}
        placeholder="Code"
      />
      {error ? <p data-testid="signin-error" className="text-sm text-flagged">{error}</p> : null}
      <Button data-testid="submit-code-btn" disabled={busy || !code.trim()} onClick={() => void verify()}>
        {busy ? 'Signing in…' : 'Sign in'}
      </Button>
      <div className="flex justify-between text-sm">
        <button type="button" data-testid="resend-code-btn" className="text-text-secondary underline"
                disabled={busy} onClick={() => void send()}>Send a new code</button>
        <button type="button" data-testid="change-email-btn" className="text-text-secondary underline"
                disabled={busy} onClick={() => { setChallenge(null); setError(null); }}>Use a different email</button>
      </div>
    </div>
  );
}
